import React, { ReactNode } from "react";
import {
    ReservationLogEntry,
    isReservationLogChangeListEntry,
    isReservationLogTextEntry,
} from "./ReservationLogEntry";
import {
    ReservationLogChangeListItem,
    isReservationLogCompositeChangeListItem,
    isReservationLogScalarChangeListItem,
} from "./ReservationLogChangeListItem";

/**
 * Renderer types
 */
export type ReservationLogEntryRenderer = (entry: ReservationLogEntry) => ReactNode | null;
export type ReservationLogChangeListItemRenderer = (
    item: ReservationLogChangeListItem,
    level: number,
    index: number
) => ReactNode | ReactNode[] | null;

/**
 * Internal registries for renderers
 */
const entryRenderers = new Map<string, ReservationLogEntryRenderer>();
const changeItemRenderers = new Map<string, ReservationLogChangeListItemRenderer>();

/**
 * Registration APIs for consumers
 * Key guidelines:
 *  - For built-in entry kinds, use keys: "ChangeList", "Text"
 *  - For custom entry kinds produced by custom deserializers, prefer a stable string, e.g. your DTO $type
 *  - For change list items, built-ins use: "Scalar", "Composite"
 *  - For custom change list items, prefer your DTO $type
 */
export function registerReservationLogEntryRenderer(kind: string, renderer: ReservationLogEntryRenderer): void {
    entryRenderers.set(kind, renderer);
}
export function registerReservationLogChangeListItemRenderer(
    kind: string,
    renderer: ReservationLogChangeListItemRenderer
): void {
    changeItemRenderers.set(kind, renderer);
}

/**
 * Try render helpers used by host components
 * Resolution order:
 *  - Built-in discriminators (ChangeList/Text, Scalar/Composite)
 *  - Custom kind (when deserializer created an object with $kind or unknown wrapper with $dto.$type)
 *  - Fallback null
 */
export function tryRenderReservationLogEntry(entry: ReservationLogEntry): ReactNode | null {
    // Built-in kinds
    if (isReservationLogChangeListEntry(entry)) {
        const r = entryRenderers.get("ChangeList");
        if (r) return r(entry);
        return defaultRenderChangeListEntry(entry);
    }
    if (isReservationLogTextEntry(entry)) {
        const r = entryRenderers.get("Text");
        if (r) return r(entry);
        return defaultRenderTextEntry(entry);
    }

    // Custom kinds
    const anyEntry: any = entry as any;
    const customKind: string | undefined =
        typeof anyEntry?.$kind === "string"
            ? anyEntry.$kind
            : typeof anyEntry?.$dto?.$type === "string"
            ? anyEntry.$dto.$type
            : undefined;

    if (customKind) {
        const r = entryRenderers.get(customKind);
        if (r) return r(entry);
    }

    return null;
}

export function tryRenderReservationLogChangeListItem(
    item: ReservationLogChangeListItem,
    level: number,
    index: number
): ReactNode | ReactNode[] | null {
    // Built-in kinds
    if (isReservationLogScalarChangeListItem(item)) {
        const r = changeItemRenderers.get("Scalar");
        if (r) return r(item, level, index);
        return defaultRenderScalarItem(item, level, index);
    }
    if (isReservationLogCompositeChangeListItem(item)) {
        const r = changeItemRenderers.get("Composite");
        if (r) return r(item, level, index);
        return defaultRenderCompositeItem(item, level, index);
    }

    // Custom kinds
    const anyItem: any = item as any;
    const customKind: string | undefined =
        typeof anyItem?.$kind === "string"
            ? anyItem.$kind
            : typeof anyItem?.$dto?.$type === "string"
            ? anyItem.$dto.$type
            : undefined;

    if (customKind) {
        const r = changeItemRenderers.get(customKind);
        if (r) return r(item, level, index);
    }

    return null;
}

/**
 * Default built-in renderers
 */

function defaultRenderTextEntry(entry: ReservationLogEntry): ReactNode {
    const e: any = entry as any;
    const text: string = e?.Text ?? "";
    if (!text) return null;
    return <div style={{ whiteSpace: "pre-wrap" }}>{text}</div>;
}

function defaultRenderChangeListEntry(entry: ReservationLogEntry): ReactNode {
    const e: any = entry as any;
    const items: ReservationLogChangeListItem[] = Array.isArray(e?.Changes) ? e.Changes : [];
    if (!items.length) return null;

    return (
        <table className="ml-2 table">
            <thead>
                <tr>
                    <th>Field</th>
                    <th>Old Value</th>
                    <th>New Value</th>
                </tr>
            </thead>
            <tbody>{renderChangeListItems(items, 0)}</tbody>
        </table>
    );
}

function renderChangeListItems(items: ReservationLogChangeListItem[], level: number): ReactNode[] {
    const rows: ReactNode[] = [];
    items.forEach((item, index) => {
        const rendered = tryRenderReservationLogChangeListItem(item, level, index);
        if (Array.isArray(rendered)) rows.push(...rendered);
        else if (rendered) rows.push(rendered);
    });
    return rows;
}

function defaultRenderScalarItem(item: ReservationLogChangeListItem, level: number, index: number): ReactNode {
    const i: any = item as any;
    return (
        <tr key={`${level}_${index}`}>
            <td style={{ paddingLeft: `${level * 1.5}rem` }}>{i.FieldName}</td>
            <td className="line-through">{i.OldValue}</td>
            <td>{i.NewValue}</td>
        </tr>
    );
}

function defaultRenderCompositeItem(
    item: ReservationLogChangeListItem,
    level: number,
    index: number
): ReactNode[] {
    const i: any = item as any;
    const children: ReservationLogChangeListItem[] = Array.isArray(i?.Changes) ? i.Changes : [];
    return [
        <tr key={`${level}_${index}`}>
            <td style={{ paddingLeft: `${level * 1.5}rem` }}>{i.FieldName}</td>
            <td></td>
            <td></td>
        </tr>,
        ...renderChangeListItems(children, level + 1),
    ];
}

/**
 * Register default built-ins at module load
 */
registerReservationLogEntryRenderer("Text", defaultRenderTextEntry);
registerReservationLogEntryRenderer("ChangeList", defaultRenderChangeListEntry);
registerReservationLogChangeListItemRenderer("Scalar", defaultRenderScalarItem);
registerReservationLogChangeListItemRenderer("Composite", defaultRenderCompositeItem);